import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Badge } from './Badge';
import { Button } from './Button';
import { Toggle } from './Toggle';
import styles from './CrashesPane.module.scss';
import {
  crashWatchService,
  type CrashCounts, type CrashDetail, type CrashEnv, type CrashLine, type CrashSummary, type CrashWatchSettings,
  type ErrorGroupDetail, type ErrorGroupSummary, type ServerCrashes,
} from '@/deploy/services/crash-watch.service';
import {
  crashFacts, exampleLabel, fmtAgo, fmtCount, fmtWhen, kindMeta, lineTones, outcomeMeta, watchdogReportFolder, watchTone,
} from './crashes.model';

// ============================================================
// Onglet « Crashs » d'une instance : crashs des 10 derniers jours (cause en une ligne, rapport
// dépliable avec les lignes de la console) et erreurs de la console regroupées (compteur, dernière
// fois, exemple dépliable). Les données viennent de la surveillance du CICD ; l'onglet ne fait que
// les relire (à l'ouverture, au bouton Rafraîchir, puis toutes les minutes).
// ============================================================

const ENV_LABELS: Record<CrashEnv, string> = { dev: 'Dev et Bêta', preprod: 'Pré-prod', prod: 'Prod' };
const REFRESH_MS = 60_000;

interface CrashesPaneProps {
  projectId: string;
  env: CrashEnv;
  identifier: string;
  /** Ouvre un dossier dans l'onglet Files (rapport complet du Watchdog). */
  onOpenFolder?: (path: string) => void;
  /** Compteurs relus, pour le badge de l'onglet. */
  onCounts?: (c: CrashCounts) => void;
}

type Loaded<T> = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ok'; data: T };

export function CrashesPane({ projectId, env, identifier, onOpenFolder, onCounts }: CrashesPaneProps) {
  const [data, setData] = useState<ServerCrashes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [withGraceful, setWithGraceful] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [openCrashes, setOpenCrashes] = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [crashDetails, setCrashDetails] = useState<Record<string, Loaded<CrashDetail>>>({});
  const [groupDetails, setGroupDetails] = useState<Record<string, Loaded<ErrorGroupDetail>>>({});
  const [settings, setSettings] = useState<CrashWatchSettings | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  // Une réponse d'un autre serveur (changement d'instance) ou plus ancienne ne remplace pas la courante
  const seq = useRef(0);
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const r = await crashWatchService.server(projectId, env, identifier, withGraceful);
      if (seq.current !== mine) return;
      setData(r);
      setError(null);
      setNow(new Date());
      onCountsRef.current?.(r);
    } catch (e) {
      if (seq.current !== mine) return;
      const msg = (e as Error).message;
      // Route inconnue : le CICD n'a pas encore la surveillance (panel mis en ligne avant lui)
      setError(msg === 'HTTP 404' ? 'le CICD ne connaît pas encore l\'onglet Crashs (sa mise à jour n\'est pas en ligne)' : msg);
    } finally {
      if (seq.current === mine) setLoading(false);
    }
  }, [projectId, env, identifier, withGraceful]);

  // Changement de serveur : on repart de zéro
  useEffect(() => {
    setData(null);
    setOpenCrashes(new Set());
    setOpenGroups(new Set());
    setCrashDetails({});
    setGroupDetails({});
  }, [projectId, env, identifier]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    let alive = true;
    crashWatchService.settings(projectId)
      .then((s) => { if (alive) setSettings(s); })
      .catch(() => { /* réglages illisibles : la section reste cachée */ });
    return () => { alive = false; };
  }, [projectId]);

  const toggleCrash = (c: CrashSummary) => {
    const opening = !openCrashes.has(c.id);
    setOpenCrashes((s) => { const n = new Set(s); if (opening) n.add(c.id); else n.delete(c.id); return n; });
    if (!opening || crashDetails[c.id]?.state === 'ok') return;
    setCrashDetails((m) => ({ ...m, [c.id]: { state: 'loading' } }));
    crashWatchService.crash(projectId, env, identifier, c.id)
      .then((d) => setCrashDetails((m) => ({ ...m, [c.id]: { state: 'ok', data: d } })))
      .catch((e) => setCrashDetails((m) => ({ ...m, [c.id]: { state: 'error', message: (e as Error).message } })));
  };

  const toggleGroup = (g: ErrorGroupSummary) => {
    const opening = !openGroups.has(g.id);
    setOpenGroups((s) => { const n = new Set(s); if (opening) n.add(g.id); else n.delete(g.id); return n; });
    if (!opening) return;
    // Relu à chaque ouverture : l'exemple tourne
    setGroupDetails((m) => ({ ...m, [g.id]: m[g.id]?.state === 'ok' ? m[g.id] : { state: 'loading' } }));
    crashWatchService.errorGroup(projectId, env, identifier, g.id)
      .then((d) => setGroupDetails((m) => ({ ...m, [g.id]: { state: 'ok', data: d } })))
      .catch((e) => setGroupDetails((m) => ({ ...m, [g.id]: { state: 'error', message: (e as Error).message } })));
  };

  const saveSetting = async (kind: 'env' | 'global', enabled: boolean) => {
    if (kind === 'global' && !enabled
      && !confirm('Couper la surveillance des crashs pour TOUS les projets et environnements ?')) return;
    setSavingSettings(true);
    try {
      const s = kind === 'global'
        ? await crashWatchService.setGlobal(projectId, enabled)
        : await crashWatchService.setEnv(projectId, env, enabled);
      setSettings(s);
      void load();
    } catch (e) {
      alert('Réglage impossible : ' + (e as Error).message);
    } finally {
      setSavingSettings(false);
    }
  };

  const watch = data?.watch;
  const crashes = data?.crashes ?? [];
  const errors = data?.errors ?? [];
  const envSetting = settings?.envs?.[env];

  return (
    <div className={styles.pane}>
      <div className={styles.watchBar}>
        {watch ? (
          <Badge tone={watchTone(watch.state)} dot>{watch.label}</Badge>
        ) : (
          <Badge tone="muted" dot>{error ? 'Surveillance illisible' : 'Chargement…'}</Badge>
        )}
        {watch?.state === 'active' && (
          <span className={styles.muted}>
            {watch.connectedSince ? `connectée ${fmtWhen(watch.connectedSince, now)}` : 'connectée'}
            {watch.analyzedLines != null && ` · ${fmtCount(watch.analyzedLines)} lignes lues`}
            {watch.droppedLines ? ` · ${fmtCount(watch.droppedLines)} lignes trop rapides comptées sans être lues` : ''}
          </span>
        )}
        <span className={styles.spacer} />
        {settings?.canConfigure && (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={12} />}
            onClick={() => setShowSettings((v) => !v)}>Réglages</Button>
        )}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />}
          loading={loading} onClick={() => void load()}>Rafraîchir</Button>
      </div>

      {showSettings && settings?.canConfigure && (
        <div className={styles.settings}>
          {!settings.configEnabled && (
            <p className={styles.settingsNote}>
              La surveillance est coupée dans la configuration du CICD (recube.crash-watch.enabled) : ces réglages n'auront
              d'effet qu'une fois rallumée là-bas.
            </p>
          )}
          <Toggle
            checked={!!envSetting?.enabled}
            disabled={savingSettings}
            onChange={(v) => void saveSetting('env', v)}
            label={`Surveiller l'environnement ${ENV_LABELS[env]}`}
            description={envSetting?.byDefault
              ? 'Réglage par défaut du CICD, jamais changé pour ce projet'
              : 'Une WebSocket de console par serveur de cet environnement, pour ce projet'}
          />
          <Toggle
            checked={settings.globalEnabled}
            disabled={savingSettings}
            onChange={(v) => void saveSetting('global', v)}
            label="Interrupteur général"
            description="Tous les projets et tous les environnements : coupé, plus aucun serveur n'est surveillé"
          />
        </div>
      )}

      {error && !data && (
        <div className={styles.errorBox}><Icon name="warn" size={14} /> Lecture impossible : {error}</div>
      )}

      <section className={styles.section}>
        <header className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            Crashs <span className={styles.sectionCount}>{crashes.length}</span>
          </h3>
          <span className={styles.muted}>{data ? `${data.retentionDays} derniers jours` : ''}</span>
          <span className={styles.spacer} />
          <label className={styles.check}>
            <input type="checkbox" checked={withGraceful} onChange={(e) => setWithGraceful(e.target.checked)} />
            Afficher aussi les arrêts propres{data?.graceful24h ? ` (${data.graceful24h} depuis 24 h)` : ''}
          </label>
        </header>
        {data && crashes.length === 0 && (
          <div className={styles.empty}>
            Aucun crash sur cette période.{withGraceful ? '' : ' Les arrêts propres (redémarrage planifié, stop en jeu) ne sont pas comptés.'}
          </div>
        )}
        {crashes.length > 0 && (
          <ul className={styles.list}>
            {crashes.map((c) => (
              <CrashRow key={c.id} crash={c} now={now} open={openCrashes.has(c.id)} detail={crashDetails[c.id]}
                onToggle={() => toggleCrash(c)} onOpenFolder={onOpenFolder} />
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <header className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            Erreurs de la console <span className={styles.sectionCount}>{errors.length}</span>
          </h3>
          <span className={styles.muted}>un même message est compté une seule fois, avec son nombre de répétitions</span>
        </header>
        {data && errors.length === 0 && <div className={styles.empty}>Aucune erreur dans la console sur cette période.</div>}
        {errors.length > 0 && (
          <ul className={styles.list}>
            {errors.map((g) => (
              <ErrorRow key={g.id} group={g} now={now} open={openGroups.has(g.id)} detail={groupDetails[g.id]}
                onToggle={() => toggleGroup(g)} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CrashRow({ crash, now, open, detail, onToggle, onOpenFolder }: {
  crash: CrashSummary; now: Date; open: boolean; detail?: Loaded<CrashDetail>;
  onToggle: () => void; onOpenFolder?: (path: string) => void;
}) {
  const kind = kindMeta(crash.kind);
  const outcome = outcomeMeta(crash.outcome);
  const facts = crashFacts(crash);
  const lines = detail?.state === 'ok' ? detail.data.lines : [];
  const folder = detail?.state === 'ok' ? watchdogReportFolder(lines) : null;
  return (
    <li className={[styles.row, !crash.counted && styles.rowMuted].filter(Boolean).join(' ')}>
      <button type="button" className={styles.rowHead} onClick={onToggle} aria-expanded={open}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={12} />
        <span className={styles.when} title={new Date(crash.at).toLocaleString('fr-FR')}>{fmtWhen(crash.at, now)}</span>
        <Badge tone={kind.tone}>{kind.label}</Badge>
        <span className={styles.cause} title={crash.cause}>{crash.cause}</span>
        {crash.repeats > 0 && <span className={styles.repeats} title="Crashs de plus dans l'heure, non détaillés">+{crash.repeats}</span>}
        <Badge tone={outcome.tone}>{outcome.label}</Badge>
      </button>
      {open && (
        <div className={styles.rowBody}>
          <div className={styles.facts}>
            {facts.map((f) => <span key={f}>{f}</span>)}
          </div>
          {kind.hint && <p className={styles.hint}>{kind.hint}</p>}
          {crash.suspect && (
            <p className={styles.hint}>Fil soupçonné par le Watchdog : <code className={styles.code}>{crash.suspect}</code></p>
          )}
          {detail?.state === 'loading' && <div className={styles.muted}>Chargement des lignes de la console…</div>}
          {detail?.state === 'error' && <div className={styles.errorBox}><Icon name="warn" size={14} /> {detail.message}</div>}
          {detail?.state === 'ok' && (
            <>
              <div className={styles.blockLabel}>
                Dernières lignes de la console avant l'arrêt{detail.data.causeIndex >= 0 ? ' (la ligne de la cause est surlignée)' : ''}
              </div>
              {lines.length > 0
                ? <LogBlock lines={lines} causeIndex={detail.data.causeIndex} />
                : <div className={styles.empty}>Aucune ligne reçue avant l'arrêt.</div>}
              {detail.data.daemon.length > 0 && (
                <div className={styles.daemon}>
                  <span className={styles.blockLabel}>Pterodactyl :</span> {detail.data.daemon.join(' · ')}
                </div>
              )}
              {folder && onOpenFolder && (
                <Button variant="secondary" size="sm" leftIcon={<Icon name="layers" size={12} />}
                  onClick={() => onOpenFolder(folder)}>
                  Ouvrir le rapport complet du Watchdog dans Files
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

function ErrorRow({ group, now, open, detail, onToggle }: {
  group: ErrorGroupSummary; now: Date; open: boolean; detail?: Loaded<ErrorGroupDetail>; onToggle: () => void;
}) {
  return (
    <li className={styles.row}>
      <button type="button" className={styles.rowHead} onClick={onToggle} aria-expanded={open}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={12} />
        <Badge tone={group.level === 'fatal' ? 'danger' : 'warning'}>{group.level === 'fatal' ? 'Fatale' : 'Erreur'}</Badge>
        <span className={styles.title} title={group.title}>{group.title}</span>
        <span className={styles.counter} title={`${fmtCount(group.total)} fois depuis la première`}>× {fmtCount(group.count)}</span>
        <span className={styles.ago} title={`Dernière fois : ${fmtWhen(group.lastAt, now)}`}>{fmtAgo(group.lastAt, now)}</span>
      </button>
      {open && (
        <div className={styles.rowBody}>
          <div className={styles.facts}>
            <span>Première fois {fmtWhen(group.firstAt, now)}</span>
            <span>Dernière fois {fmtWhen(group.lastAt, now)}</span>
            <span>{fmtCount(group.count)} fois sur la période</span>
          </div>
          {group.overflow && (
            <p className={styles.hint}>
              Trop de messages différents pour ce serveur : les nouveaux sont comptés ensemble ici, sans exemple.
            </p>
          )}
          {detail?.state === 'loading' && <div className={styles.muted}>Chargement de l'exemple…</div>}
          {detail?.state === 'error' && <div className={styles.errorBox}><Icon name="warn" size={14} /> {detail.message}</div>}
          {detail?.state === 'ok' && detail.data.examples.length === 0 && !group.overflow && (
            <div className={styles.empty}>Pas d'exemple gardé.</div>
          )}
          {detail?.state === 'ok' && detail.data.examples.map((ex, i) => (
            <div key={ex.at + i}>
              <div className={styles.blockLabel}>{exampleLabel(ex.at, now)}</div>
              <LogBlock lines={ex.lines.map((text) => ({ at: null, text, after: false }))} causeIndex={0} />
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

/** Lignes de console sur fond sombre, comme l'onglet Console ; la ligne de la cause est surlignée et centrée. */
function LogBlock({ lines, causeIndex }: { lines: CrashLine[]; causeIndex: number }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const causeRef = useRef<HTMLDivElement>(null);
  const tones = lineTones(lines, causeIndex);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const el = causeRef.current;
    if (!box) return;
    if (el) box.scrollTop = Math.max(0, el.offsetTop - box.clientHeight / 2);
    else box.scrollTop = box.scrollHeight;
  }, [lines, causeIndex]);
  return (
    <div className={styles.log} ref={boxRef}>
      {lines.map((l, i) => (
        <div key={i} ref={i === causeIndex ? causeRef : undefined}
          className={[styles.logLine, styles[`log_${tones[i]}`]].join(' ')}>
          <span className={styles.logTime}>
            {l.at ? new Date(l.at).toLocaleTimeString('fr-FR', { hour12: false }) : ''}
          </span>
          <span className={styles.logText}>{l.text}</span>
        </div>
      ))}
    </div>
  );
}
